import { Button } from '@/components/ui/Button';
import { ItemsPerRowSelector } from '@/components/ui/custom-ui/ItemsPerRowSelector';
import { ErrorBoundaryFallbackRender } from '@/components/ui/ErrorBoundaryFallback';
import { PolygonOverlayWrapper } from '@/components/ui/polygon/PolygonOverlayWrapper';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { DateTimePicker } from '@/components/ui/time-picker/DateTimePicker';
import config from '@/config';
import { Device } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { MediaKind } from '@/models/device.models';
import { MediaMimeRootType } from '@/models/media';
import { SiteConfig } from '@/models/site.models';
import { UTC_ZONE } from '@/models/time.models';
import { RoiConfig } from '@/models/zone.models';
import { getMediaEndpointUrl } from '@/utils/media.functions';
import { getGridColsClass } from '@/utils/ui.function';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams, useSearchParams } from 'react-router';
import {
  deviceMediaBeforeTimeQuery,
  deviceMediaFromTimeQuery,
  devicesQuery,
  devicesQueryNoApiKeyDescription,
} from '../graphql/query';

const DEFAULT_MEDIA_LIMIT = 50;
const DEFAULT_COLUMNS = 4;
const TIME_PARAM = 'time';
const KIND_PARAM = 'kind';
const LIMIT_OPTIONS = [25, 50, 100, 200];
const MEDIA_KIND_OPTIONS = [
  MediaKind.ORIGINAL,
  MediaKind.PROCESSED,
  MediaKind.MASKED,
];

export default function DeviceMediaPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeParam = searchParams.get(TIME_PARAM);
  const kindParam = searchParams.get(KIND_PARAM);

  const [numberOfColumns, setNumberOfColumns] = useState(DEFAULT_COLUMNS);
  const [mediaLimit, setMediaLimit] = useState(DEFAULT_MEDIA_LIMIT);
  const [mediaKind, setMediaKind] = useState<MediaKind>(() => {
    if (kindParam && MEDIA_KIND_OPTIONS.includes(kindParam as MediaKind)) {
      return kindParam as MediaKind;
    }
    return MediaKind.ORIGINAL;
  });
  const [lookupDirection, setLookupDirection] = useState<
    'forward' | 'backward'
  >('forward');
  const initialDateTime = useMemo(() => {
    if (!timeParam) {
      return DateTime.now().minus({ seconds: 60 });
    }
    const parsed = DateTime.fromISO(timeParam);
    return parsed.isValid ? parsed : DateTime.now().minus({ seconds: 60 });
  }, [timeParam]);
  const [cursorDateTime, setCursorDateTime] =
    useState<DateTime>(initialDateTime);
  const [selectedDateTime, setSelectedDateTime] =
    useState<DateTime>(initialDateTime);

  useEffect(() => {
    const iso = selectedDateTime.toISO();
    if (!iso || (timeParam === iso && kindParam === mediaKind)) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(TIME_PARAM, iso);
    nextParams.set(KIND_PARAM, mediaKind);
    setSearchParams(nextParams, { replace: true });
  }, [
    kindParam,
    mediaKind,
    searchParams,
    selectedDateTime,
    setSearchParams,
    timeParam,
  ]);

  const { data: device, isLoading: deviceLoading } = useQuery({
    queryKey: ['DeviceMediaDevice', id],
    queryFn: () =>
      execute({
        query: config.apiKey.hasDescription
          ? devicesQuery
          : devicesQueryNoApiKeyDescription,
        variables: {
          where: {
            id: {
              _eq: id,
            },
          },
        },
      }),
    select: (data) => data.device?.[0] as Device | undefined,
    enabled: !!id,
  });

  const cursorTime = cursorDateTime.toISO() ?? DateTime.now().toISO();
  const {
    data: mediaData,
    isLoading: mediaLoading,
    isFetching: mediaFetching,
    isPlaceholderData,
  } = useQuery({
    queryKey: [
      'DeviceMedia',
      id,
      cursorTime,
      mediaLimit,
      mediaKind,
      lookupDirection,
    ],
    queryFn: () => {
      if (lookupDirection === 'forward') {
        return execute({
          query: deviceMediaFromTimeQuery,
          variables: {
            deviceId: id as string,
            fromTime: cursorTime,
            limit: mediaLimit,
            kind: mediaKind,
          },
        });
      }
      return execute({
        query: deviceMediaBeforeTimeQuery,
        variables: {
          deviceId: id as string,
          beforeTime: cursorTime,
          limit: mediaLimit,
          kind: mediaKind,
        },
      });
    },
    enabled: !!id,
  });

  const rawMediaItems = mediaData?.media ?? [];
  const mediaItems = useMemo(
    () =>
      lookupDirection === 'backward'
        ? [...rawMediaItems].reverse()
        : rawMediaItems,
    [lookupDirection, rawMediaItems],
  );
  const deviceTimezone = (device?.site?.config as SiteConfig)?.Timezone;
  const polygonData = useMemo(() => {
    if (mediaKind !== MediaKind.PROCESSED) {
      return [];
    }
    return (device?.rois ?? []).map((roi) => {
      return {
        polygon: (roi.config as RoiConfig).Points,
        name: roi.name,
      };
    });
  }, [device?.rois, mediaKind]);

  useEffect(() => {
    if (mediaFetching || isPlaceholderData || mediaItems.length === 0) {
      return;
    }
    const firstDetectionTime = DateTime.fromISO(mediaItems[0].time);
    if (!firstDetectionTime.isValid) {
      return;
    }
    const firstDetectionIso = firstDetectionTime.toISO();
    if (firstDetectionIso && firstDetectionIso === selectedDateTime.toISO()) {
      return;
    }
    setSelectedDateTime(firstDetectionTime);
  }, [isPlaceholderData, mediaFetching, mediaItems, selectedDateTime]);

  const selectedTimeLabel = selectedDateTime
    .setZone(deviceTimezone ?? UTC_ZONE)
    .toFormat('dd LLL yyyy, HH:mm:ss');
  const endTimeLabel =
    mediaItems.length > 0
      ? DateTime.fromISO(mediaItems[mediaItems.length - 1].time, {
          zone: UTC_ZONE,
        })
          .setZone(deviceTimezone ?? UTC_ZONE)
          .toFormat('HH:mm:ss')
      : null;
  const directionLabel = 'from';

  const handleDateTimeChange = (dateTime?: DateTime) => {
    const nextDateTime = dateTime ?? DateTime.now();
    setCursorDateTime(nextDateTime);
    setSelectedDateTime(nextDateTime);
    setLookupDirection('forward');
  };

  const handleKindChange = (value: string) => {
    setMediaKind(value as MediaKind);
    setLookupDirection('forward');
  };

  const handlePreviousPage = () => {
    setLookupDirection('backward');
    if (mediaItems.length === 0) {
      return;
    }
    setCursorDateTime(DateTime.fromISO(mediaItems[0].time));
  };

  const handleNextPage = () => {
    if (mediaItems.length === 0) {
      return;
    }
    const nextTime = DateTime.fromISO(
      mediaItems[mediaItems.length - 1].time,
    ).plus({ milliseconds: 1 });
    setLookupDirection('forward');
    setCursorDateTime(nextTime);
  };

  const isLoading = deviceLoading || mediaLoading || mediaFetching;
  const canGoBackward =
    mediaItems.length > 0
      ? !(lookupDirection === 'backward' && mediaItems.length < mediaLimit)
      : lookupDirection === 'forward';
  const canGoForward =
    mediaItems.length > 0 &&
    !(lookupDirection === 'forward' && mediaItems.length < mediaLimit);

  return (
    <ErrorBoundary fallbackRender={ErrorBoundaryFallbackRender}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">
              {device?.name ?? 'Device Media'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DateTimePicker
              timezoneSelectionEnabled={true}
              extraTimezone={deviceTimezone}
              defaultDate={selectedDateTime}
              onChange={handleDateTimeChange}
            />
            <Select value={mediaKind} onValueChange={handleKindChange}>
              <SelectTrigger className="w-[140px]" size={'sm'}>
                <SelectValue placeholder="Media kind" />
              </SelectTrigger>
              <SelectContent>
                {MEDIA_KIND_OPTIONS.map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {kind}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handlePreviousPage}
                disabled={isLoading || mediaFetching || !canGoBackward}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleNextPage}
                disabled={isLoading || mediaFetching || !canGoForward}
                aria-label="Next page"
              >
                <ChevronRight />
              </Button>
            </div>
            <ItemsPerRowSelector
              value={numberOfColumns}
              onChange={setNumberOfColumns}
            />
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">Count</span>
              <Select
                value={`${mediaLimit}`}
                onValueChange={(value) => setMediaLimit(Number(value))}
              >
                <SelectTrigger className="w-[90px]" size={'sm'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIMIT_OPTIONS.map((limit) => (
                    <SelectItem key={limit} value={`${limit}`}>
                      {limit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="text-muted-foreground text-sm">
          Showing up to {mediaLimit} items {directionLabel}{' '}
          <span className="font-semibold">{selectedTimeLabel}</span>
          {endTimeLabel ? (
            <>
              <ArrowRight className="mx-2 inline-block size-4" />
              <span className="font-semibold">{endTimeLabel}</span>
            </>
          ) : null}
        </div>

        <div className="relative rounded-md border p-4">
          {isLoading && (
            <>
              <div className="bg-secondary absolute inset-0 opacity-25"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Spinner className="h-8" />
              </div>
            </>
          )}
          {mediaItems.length === 0 && !isLoading ? (
            <div className="text-muted-foreground flex h-24 items-center justify-center text-sm">
              No media found after time.
            </div>
          ) : (
            <div className={`grid ${getGridColsClass(numberOfColumns)} gap-4`}>
              {mediaItems.map((media) => {
                const mediaEndpointUrl = getMediaEndpointUrl({
                  mediaId: media.mediaId ?? '',
                  timestamp: media.time,
                });
                const localTime = DateTime.fromISO(media.time, {
                  zone: UTC_ZONE,
                })
                  .setZone(deviceTimezone ?? UTC_ZONE)
                  .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
                return (
                  <div
                    key={media.id}
                    className="flex min-h-24 flex-col rounded-t-md border p-0"
                  >
                    <div className="bg-secondary flex flex-col rounded-t-md px-2 pt-3 pb-1">
                      <span className="truncate leading-none font-semibold">
                        {localTime}
                      </span>
                      <div className="text-muted-foreground truncate text-sm leading-5">
                        {media.kind}
                      </div>
                    </div>
                    <div className="flex flex-1 items-center justify-center">
                      <PolygonOverlayWrapper
                        mediaEndpointUrl={mediaEndpointUrl}
                        polygonData={polygonData}
                        isVideo={media.mimeRootType === MediaMimeRootType.VIDEO}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}
